import React, { useState, useMemo } from "react";
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Pie, Line } from "react-chartjs-2";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const PlatformMetrics = ({ data, impressionsData }) => {
  const [chartType, setChartType] = useState("pie"); // 'pie' or 'trend'
  const [selectedDate, setSelectedDate] = useState("aggregate"); // 'aggregate' or specific date
  const [dataType, setDataType] = useState("clicks"); // 'clicks' or 'impressions'

  // Format date for display
  const formatDate = (dateStr) => {
    const [year, month, day] = dateStr.split("-");
    return `${parseInt(month)}/${parseInt(day)}/${year}`;
  };

  // Use the selected data type
  const activeData =
    dataType === "impressions" && impressionsData ? impressionsData : data;

  const availableDates = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];
    return [...new Set(activeData.map((item) => item.date))].sort(
      (a, b) => new Date(a) - new Date(b)
    );
  }, [activeData]);

  const dateLabels = useMemo(() => {
    return availableDates.map((date) => formatDate(date));
  }, [availableDates]);

  const platformTypes = useMemo(() => {
    if (!activeData || activeData.length === 0) return [];

    // Get all unique platform types across all dates
    const platforms = new Set();
    activeData.forEach((item) => {
      Object.keys(item).forEach((key) => {
        if (
          key !== "date" &&
          item[key] !== null &&
          item[key] !== 0 &&
          item[key] !== "0"
        ) {
          platforms.add(key);
        }
      });
    });

    return Array.from(platforms);
  }, [activeData]);

  // Process data for the pie chart
  const pieChartData = useMemo(() => {
    if (!activeData || activeData.length === 0 || platformTypes.length === 0)
      return null;

    let selectedData;
    let dateLabel;

    // Handle different date selection options
    if (selectedDate === "aggregate") {
      // Aggregate data across all dates
      const aggregatedData = {};
      platformTypes.forEach((platform) => {
        aggregatedData[platform] = 0;
      });

      activeData.forEach((item) => {
        platformTypes.forEach((platform) => {
          if (item[platform]) {
            aggregatedData[platform] += Number(item[platform]);
          }
        });
      });

      selectedData = aggregatedData;
      dateLabel = "All Dates (Aggregated)";
    } else {
      // Use a specific date
      const dateData = activeData.find((item) => item.date === selectedDate);

      if (!dateData) return null;

      selectedData = dateData;
      dateLabel = formatDate(selectedDate);
    }

    const platforms = platformTypes
      .map((platform) => ({
        platform,
        value: Number(selectedData[platform] || 0),
      }))
      .filter((item) => item.value > 0);

    // Sort by value in descending order
    platforms.sort((a, b) => b.value - a.value);

    const total = platforms.reduce((sum, item) => sum + item.value, 0);

    const labels = platforms.map((item) => item.platform);
    const values = platforms.map((item) => item.value);
    const percentages = platforms.map((item) =>
      ((item.value / total) * 100).toFixed(1)
    );

    const pieColors = [
      "rgba(54, 162, 235, 0.8)", // Blue
      "rgba(255, 99, 132, 0.8)", // Red
      "rgba(255, 205, 86, 0.8)", // Yellow
      "rgba(75, 192, 192, 0.8)", // Green
      "rgba(153, 102, 255, 0.8)", // Purple
      "rgba(255, 159, 64, 0.8)", // Orange
      "rgba(201, 203, 207, 0.8)", // Grey
    ];

    return {
      dateLabel,
      total,
      chartData: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: pieColors.slice(0, labels.length),
            borderColor: "rgba(255, 255, 255, 0.5)",
            borderWidth: 1,
          },
        ],
      },
      platforms: platforms.map((item, index) => ({
        ...item,
        percentage: percentages[index],
        color: pieColors[index % pieColors.length],
      })),
    };
  }, [activeData, availableDates, platformTypes, selectedDate]);

  // Process data for the trend chart (all dates)
  const trendChartData = useMemo(() => {
    if (
      !activeData ||
      activeData.length === 0 ||
      platformTypes.length === 0 ||
      availableDates.length === 0
    ) {
      return null;
    }

    // For each platform, create a dataset of values over time
    const datasets = platformTypes
      .map((platform, index) => {
        const platformData = availableDates.map((date) => {
          const dateData = activeData.find((item) => item.date === date);
          return dateData ? Number(dateData[platform] || 0) : 0;
        });

        // Skip platforms with all zeros
        if (platformData.every((val) => val === 0)) {
          return null;
        }

        const colors = [
          { bg: "rgba(54, 162, 235, 0.2)", border: "rgba(54, 162, 235, 1)" }, // Blue
          { bg: "rgba(255, 99, 132, 0.2)", border: "rgba(255, 99, 132, 1)" }, // Red
          { bg: "rgba(255, 205, 86, 0.2)", border: "rgba(255, 205, 86, 1)" }, // Yellow
          { bg: "rgba(75, 192, 192, 0.2)", border: "rgba(75, 192, 192, 1)" }, // Green
          { bg: "rgba(153, 102, 255, 0.2)", border: "rgba(153, 102, 255, 1)" }, // Purple
          { bg: "rgba(255, 159, 64, 0.2)", border: "rgba(255, 159, 64, 1)" }, // Orange
          { bg: "rgba(201, 203, 207, 0.2)", border: "rgba(201, 203, 207, 1)" }, // Grey
        ];

        const colorIndex = index % colors.length;

        return {
          label: platform,
          data: platformData,
          backgroundColor: colors[colorIndex].bg,
          borderColor: colors[colorIndex].border,
          borderWidth: 2,
          tension: 0.1,
          pointRadius: 3,
        };
      })
      .filter(Boolean); // Remove null entries

    return {
      labels: dateLabels,
      datasets,
    };
  }, [activeData, availableDates, dateLabels, platformTypes]);

  if (!data || data.length === 0) {
    return (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          <h3 className="text-lg font-medium">No Platform Data Available</h3>
          <p className="mt-2">
            Please upload platform distribution data to view this chart.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">
            Platform Distribution
          </h2>
          <p className="text-sm text-gray-400">
            Analyzing audience by platform type
          </p>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setChartType("pie")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              chartType === "pie"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Distribution
          </button>
          <button
            onClick={() => setChartType("trend")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              chartType === "trend"
                ? "bg-blue-600 text-white"
                : "bg-gray-700 text-gray-300 hover:bg-gray-600"
            }`}
          >
            Trends
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mb-4">
        {chartType === "pie" && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-1">
                Date
              </label>
              <select
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="aggregate">Aggregate (All Dates)</option>
                {availableDates.map((date) => (
                  <option key={date} value={date}>
                    {formatDate(date)}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {impressionsData && (
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-1">
              Data Type
            </label>
            <select
              value={dataType}
              onChange={(e) => setDataType(e.target.value)}
              className="bg-gray-700 text-gray-200 rounded-md px-3 py-2 text-sm border border-gray-600 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="clicks">Clicks</option>
              <option value="impressions">Impressions</option>
            </select>
          </div>
        )}
      </div>

      <div className="bg-gray-800 p-6 rounded-lg">
        {chartType === "pie" && pieChartData ? (
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-1/2">
              <div className="mb-4 text-center">
                <h3 className="text-lg font-medium text-gray-200">
                  Platform Distribution ({pieChartData.dateLabel})
                </h3>
                <p className="text-sm text-gray-400">
                  Total: {pieChartData.total.toLocaleString()}
                </p>
              </div>
              <div className="w-full h-80">
                <Pie
                  data={pieChartData.chartData}
                  options={{
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: "right",
                        labels: {
                          color: "#FFFFFF",
                          font: {
                            size: 12,
                          },
                          usePointStyle: true,
                          padding: 15,
                          generateLabels: (chart) => {
                            const data = chart.data;
                            if (data.labels.length && data.datasets.length) {
                              return data.labels.map((label, i) => {
                                const value = data.datasets[0].data[i];
                                const percentage = (
                                  (value / pieChartData.total) *
                                  100
                                ).toFixed(1);

                                return {
                                  text: `${label} (${percentage}%)`,
                                  fillStyle:
                                    data.datasets[0].backgroundColor[i],
                                  hidden: !chart.getDataVisibility(i),
                                  index: i,
                                  fontColor: "#FFFFFF",
                                  color: "#FFFFFF",
                                  strokeStyle: "#000000",
                                  lineWidth: 1,
                                };
                              });
                            }
                            return [];
                          },
                          color: "#FFFFFF",
                          fontColor: "#FFFFFF",
                        },
                        labels: {
                          color: "#FFFFFF",
                          fontColor: "#FFFFFF",
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context) {
                            const value = context.raw;
                            const total = pieChartData.total;
                            return `${
                              context.label
                            }: ${value.toLocaleString()} (${(
                              (value / total) *
                              100
                            ).toFixed(1)}%)`;
                          },
                        },
                        backgroundColor: "#374151",
                        titleColor: "#E5E7EB",
                        bodyColor: "#E5E7EB",
                        padding: 12,
                        cornerRadius: 4,
                      },
                    },
                  }}
                />
              </div>
            </div>

            <div className="lg:w-1/2">
              <h3 className="text-lg font-medium text-gray-200 mb-4">
                Platform Details
              </h3>
              <div className="bg-gray-750 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-700">
                    <thead className="bg-gray-700">
                      <tr>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                        >
                          Platform
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                        >
                          Count
                        </th>
                        <th
                          scope="col"
                          className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider"
                        >
                          Percentage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-gray-800 divide-y divide-gray-700">
                      {pieChartData.platforms.map((platform, index) => (
                        <tr
                          key={platform.platform}
                          className={
                            index % 2 === 0 ? "bg-gray-800" : "bg-gray-750"
                          }
                        >
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-2"
                                style={{ backgroundColor: platform.color }}
                              ></div>
                              <span className="text-gray-300">
                                {platform.platform}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {platform.value.toLocaleString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                            {platform.percentage}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        ) : chartType === "trend" && trendChartData ? (
          <div>
            <div className="mb-4 text-center">
              <h3 className="text-lg font-medium text-gray-200">
                Platform Trends Over Time
              </h3>
              <p className="text-sm text-gray-400">
                Date range: {dateLabels[0]} to{" "}
                {dateLabels[dateLabels.length - 1]}
              </p>
            </div>
            <div className="h-96">
              <Line
                data={trendChartData}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "top",
                      labels: {
                        color: "#E5E7EB",
                        font: {
                          size: 12,
                        },
                        usePointStyle: true,
                        padding: 15,
                      },
                    },
                    tooltip: {
                      backgroundColor: "#374151",
                      titleColor: "#E5E7EB",
                      bodyColor: "#E5E7EB",
                      padding: 12,
                      cornerRadius: 4,
                    },
                  },
                  scales: {
                    y: {
                      ticks: {
                        color: "#E5E7EB",
                        font: {
                          size: 11,
                        },
                      },
                      grid: {
                        color: "rgba(229, 231, 235, 0.1)",
                      },
                    },
                    x: {
                      ticks: {
                        color: "#E5E7EB",
                        font: {
                          size: 11,
                        },
                      },
                      grid: {
                        color: "rgba(229, 231, 235, 0.1)",
                      },
                    },
                  },
                }}
              />
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-gray-400">
            No data available to display.
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformMetrics;
